import { memo, useState, useCallback } from 'react';
import { Button, Separator } from '@flote/ui';
import { Loader2 } from 'lucide-react';
import { TaskTypeSelector, type TaskCreationType } from './TaskTypeSelector';
import { useTaskForm, type TaskFormData } from './hooks';
import { PickupSection } from './sections/PickupSection';
import { DropoffSection } from './sections/DropoffSection';
import type { Team, Recipient, Destination, B2BWaveOrderSummary, Driver } from '@flote/contracts/http';

export type { TaskFormData };

interface TaskFormProps {
  onSubmit: (data: TaskFormData) => Promise<void>;
  isProcessing?: boolean;
  teams: Team[];
  recipients: Recipient[];
  destinations: Destination[];
  orders?: B2BWaveOrderSummary[];
  onFetchDriversByTeam?: (teamId: string) => Promise<Driver[]>;
}

export const TaskForm = memo<TaskFormProps>(({
  onSubmit,
  isProcessing = false,
  teams,
  recipients,
  destinations,
  orders = [],
  onFetchDriversByTeam,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useTaskForm({
    recipients,
    destinations,
    orders,
    onFetchDriversByTeam,
  });

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.validate()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const taskRequest = form.buildTaskRequest();
      console.log('Submitting task request:', taskRequest);
      await onSubmit(taskRequest);
    } finally {
      setIsSubmitting(false);
    }
  }, [form, onSubmit]);

  const processing = isSubmitting || isProcessing;

  const getSubmitButtonText = () => {
    if (processing) {
      return form.taskType === 'linked' ? 'Creating Tasks...' : 'Creating Task...';
    }
    if (form.taskType === 'linked') {
      return 'Create Pickup + Dropoff Tasks';
    }
    if (form.taskType === 'pickup') {
      return 'Create Pickup Task';
    }
    return 'Create Dropoff Task';
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Task Type Selection */}
      <TaskTypeSelector
        value={form.taskType}
        onValueChange={form.setTaskType as (value: TaskCreationType) => void}
        disabled={processing}
      />

      <Separator />

      {/* Pickup Section */}
      {form.needsPickupSection && (
        <>
          <PickupSection
            pickup={form.pickup}
            // Team/Driver
            teams={teams}
            drivers={form.pickupDrivers}
            isLoadingDrivers={form.isLoadingPickupDrivers}
            onTeamChange={form.setPickupTeamId}
            onDriverChange={form.setPickupDriverId}
            onAutoAssignChange={form.setPickupAutoAssign}
            // Recipient
            recipients={recipients}
            selectedRecipient={form.selectedPickupRecipient}
            onRecipientIdChange={form.setPickupRecipientId}
            onRecipientModeChange={form.setPickupRecipientMode}
            onRecipientChange={form.setPickupRecipient}
            // Destination
            destinations={destinations}
            selectedDestination={form.selectedPickupDestination}
            onDestinationIdChange={form.setPickupDestinationId}
            onDestinationModeChange={form.setPickupDestinationMode}
            onAddressChange={form.setPickupAddress}
            onAddressSelect={form.handlePickupAddressSelect}
            // Notes, Time, Requirements
            onNotesChange={form.setPickupNotes}
            onTimeWindowChange={form.setPickupTimeWindow}
            onCompletionRequirementsChange={form.setPickupCompletionRequirements}
            // Misc
            errors={form.errors}
            disabled={processing}
            isLinkedTask={form.taskType === 'linked'}
            noTeamValue={form.NO_TEAM_VALUE}
            noDriverValue={form.NO_DRIVER_VALUE}
            showDriverSelect={!!onFetchDriversByTeam}
          />
          <Separator />
        </>
      )}

      {/* Dropoff Section */}
      {form.needsDropoffSection && (
        <DropoffSection
          dropoff={form.dropoff}
          orders={orders}
          selectedOrder={form.selectedOrder}
          onOrderChange={form.setDropoffOrderId}
          teams={teams}
          drivers={form.drivers}
          isLoadingDrivers={form.isLoadingDrivers}
          onTeamChange={form.setDropoffTeamId}
          onDriverChange={form.setDropoffDriverId}
          onAutoAssignChange={form.setDropoffAutoAssign}
          recipients={recipients}
          selectedRecipient={form.selectedRecipient}
          onRecipientIdChange={form.setDropoffRecipientId}
          onRecipientModeChange={form.setDropoffRecipientMode}
          onRecipientChange={form.setDropoffRecipient}
          destinations={destinations}
          selectedDestination={form.selectedDestination}
          onDestinationIdChange={form.setDropoffDestinationId}
          onDestinationModeChange={form.setDropoffDestinationMode}
          onAddressChange={form.setDropoffAddress}
          onAddressSelect={form.handleDropoffAddressSelect}
          onNotesChange={form.setDropoffNotes}
          onTimeWindowChange={form.setDropoffTimeWindow}
          onCompletionRequirementsChange={form.setDropoffCompletionRequirements}
          errors={form.errors}
          disabled={processing}
          isLinkedTask={form.taskType === 'linked'}
          noTeamValue={form.NO_TEAM_VALUE}
          noOrderValue={form.NO_ORDER_VALUE}
          noDriverValue={form.NO_DRIVER_VALUE}
          orderRequired={form.ORDER_REQUIRED}
          showDriverSelect={!!onFetchDriversByTeam}
        />
      )}

      {/* Submit Button */}
      <div className="flex pt-2">
        <Button type="submit" disabled={processing} className="w-full">
          {processing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {getSubmitButtonText()}
        </Button>
      </div>
    </form>
  );
});

TaskForm.displayName = 'TaskForm';
